import React, { useState, useRef, useEffect } from 'react';
import '../styles/components/_select-dropdown.scss';

interface SelectDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  className = '',
  disabled = false,
  ariaLabel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'ArrowDown' && isOpen) {
      e.preventDefault();
      const firstOption = containerRef.current?.querySelector('[data-option]:first-of-type') as HTMLButtonElement;
      firstOption?.focus();
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, optionValue: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(optionValue);
      setIsOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextOption = (e.currentTarget as HTMLElement).nextElementSibling as HTMLButtonElement;
      nextOption?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevOption = (e.currentTarget as HTMLElement).previousElementSibling as HTMLButtonElement;
      prevOption?.focus();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`select-dropdown ${className}`}
      data-disabled={disabled}
    >
      <button
        ref={buttonRef}
        type="button"
        className="select-dropdown__button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span className="select-dropdown__label">{displayLabel}</span>
        <span className="material-symbols-outlined select-dropdown__arrow">expand_more</span>
      </button>

      {isOpen && !disabled && (
        <div className="select-dropdown__menu" role="listbox">
          {options.map((option, index) => (
            <button
              key={`${option.value}-${index}`}
              type="button"
              className={`select-dropdown__option ${value === option.value ? 'select-dropdown__option--selected' : ''}`}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              data-option
              role="option"
              aria-selected={value === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
